<template>
  <div id="app" :style="cssVariables">
    <ErrorMessage
      @dismiss-alert="formSubmitStatus = FormSubmitEnum.NOT_SUBMITTED"
      v-if="formSubmitStatus == FormSubmitEnum.ERROR"
      >The system is temporarily unavailable. Please try again later.
    </ErrorMessage>
    <Header :header="headerData" />
    <Hero v-if="content.heroImageFlag" :banner="heroData" />
    <template v-if="formSubmitStatus == FormSubmitEnum.EXPIRED">
      <Introduction :introduction="introData" :showHeadlineOnly="true" />
      <Expiry @reset-view="resetView" :thankyou="confirmationData" />
    </template>
    <template v-else-if="formSubmitStatus != FormSubmitEnum.SUCCESS && !confirmationData.previewConfirmation">
      <Introduction :introduction="introData" />
      <!-- <Summary :program="programSummaryData" /> -->
      <Selection
          @update-selected-policy="updateSelectedPolicy"
          @submit-selection="submitSelection"
          @scroll-to="scrollTo"          
          :policies="policiesData"
          :fundDetails="fundDetailsData"
          :fundNames="fundNames"
          :disclosure="disclosureData"
          :lastSelectedFund="lastSelectedFund"
          :defaultToggleState="defaultToggleState"
          :showOptOut="showOptOut"
        /> 
        <Faq ref="Learn More" v-if="content.faqFlag && content.faqs" :faq="faqsData" :header="content.faqMainHeader" @scroll-to="scrollTo"/>
      <Footer :footer="footerData" :socials="socialData" />
    </template>
    <template v-else>
      <!-- <Introduction :introduction="introData" :showHeadlineOnly="true" /> -->
      <ThankYou @reset-view="resetView" :thankyou="confirmationData" />
      <Footer :footer="footerData" :socials="socialData" />
    </template>
  </div>
</template>

<script>
const FormSubmitEnum = {
  ERROR: -1,
  NOT_SUBMITTED: 0,
  IN_PROGRESS: 1,
  SUCCESS: 2,
  EXPIRED: 3,
};
import ErrorMessage from "./components/organisms/errorMessage";
import Header from "./components/organisms/header";
import Hero from "./components/organisms/hero";
import Introduction from "./components/organisms/introduction";
import Summary from "./components/organisms/summary";
import Selection from "./components/organisms/selection";
import Faq from "./components/organisms/faq";
import ThankYou from "./components/organisms/thankyou";
import Expiry from "./components/organisms/expiry";
import Footer from "./components/organisms/footer";
import axios from "axios";
import { replacePlaceholders, prefixHTTP } from "./utils/index.js";

export default {
  name: "App",
  components: {
    ErrorMessage,
    Header,
    Hero,
    Introduction,
    Summary,
    Selection,
    Faq,
    ThankYou,
    Expiry,
    Footer,
  },
  inject: ["contentData", "brandData", "vueData"],
  data() {
    return {
      maxPolicy: 12,
      lastSelectedFund: "",
      savedPoliciesLoaded: false,
      showOptOut: false,
      brand: this.brandData,
      additionalAttributes: this.vueData.additionalAttributes,
      content: this.contentData,
      mainframeData: this.vueData,
      aemPreview: this.vueData.sourceRequestApp == "INFOCUS_UI",
      formSubmitStatus:
        this.vueData.transactionId === "EXPIRED" ? FormSubmitEnum.EXPIRED : FormSubmitEnum.NOT_SUBMITTED,
      fundNames: this.vueData.additionalAttributes.cusipClassList
        ? this.vueData.additionalAttributes.cusipClassList.slice(0, 1).map((e) => {
            return { name: e.cusipClassName, security: e.security, selectedPolicy: "" };
          })
        : [],
      policies: this.contentData.policies ? this.contentData.policies.slice(0, this.maxPolicy) : [],
      defaultToggleState:
        this.vueData.additionalAttributes.cusipClassList.length > 1
          ? this.contentData.yourHoldingsToggleButtonDefaultOn
          : true,
      FormSubmitEnum,
    };
  },

  computed: {
    transactionId() {
      let transactionId = "";
      try {
        const queryString = window.location.search.substring(1);
        transactionId = atob(queryString).substring(1);
        if (transactionId == "")
          transactionId = `transactionId=${this.vueData.transactionId}&emailToAddress=${this.vueData.preference.deliveryChannel}`;
      } catch (e) {}
      return transactionId;
    },

    policySelections() {
      return (
        "&policy=" +
        this.fundNames.map((fund) => fund.selectedPolicy).join("|") +
        "&cusip=" +
        this.fundNames.map((fund) => fund.security).join("|")
      );
    },

    cssVariables() {
      const {
        primaryText,
        secondaryText,
        primaryLink,
        micrositeTypeStyle,
        headerBar,
        button,
        buttonText,
        backgroundAccent,
      } = this.brandData;

      return {
        "--primaryText": primaryText,
        "--secondaryText": secondaryText,
        "--primaryLink": primaryLink,
        "--micrositeTypeStyle": micrositeTypeStyle,
        "--headerBar": headerBar,
        "--button": button,
        "--buttonText": buttonText,
        "--backgroundAccent": backgroundAccent,
      };
    },
    hasHeader() {
      return this.headerData.logo;
    },
    headerData() {
      const { micrositeLogo, brandName } = this.brand;
      const { logoAlignment, logoSize, heroImageFlag } = this.content;

      return {
        logo: micrositeLogo,
        alt: brandName,
        logoAlignment: logoAlignment,
        logoSize: logoSize,
        hasHero: heroImageFlag,
      };
    },
    heroData() {
      const { heroImage, heroImageAltText, heroImageSize } = this.content;
      return {
        heroImage,
        heroImageAltText: replacePlaceholders(heroImageAltText, this.mainframeData),
        heroImageSize,
      };
    },
    introData() {
      const { headlineText, headlineSubheaderText } = this.content;
      return {
        headlineText: replacePlaceholders(headlineText, this.mainframeData),
        headlineSubheaderText: replacePlaceholders(headlineSubheaderText, this.mainframeData),
      };
    },
    programSummaryData() {
      const { howTheProgramWorksHeader, howTheProgramWorksText, programSummaryButtonLabel, programSummaryButtonLink } =
        this.content;
      return {
        howTheProgramWorksHeader: replacePlaceholders(howTheProgramWorksHeader, this.mainframeData),
        howTheProgramWorksText: replacePlaceholders(howTheProgramWorksText, this.mainframeData),
        programSummaryButtonLabel: replacePlaceholders(programSummaryButtonLabel, this.mainframeData),
        programSummaryButtonLink: prefixHTTP(programSummaryButtonLink),
      };
    },
    fundDetailsData() {
      const {
        yourHoldingsHeading,
        yourHoldingsToggleButtonLabel,
        yourHoldingsText,
        yourHoldingsToggleButtonDefaultOn,
        policySelectionHeading,
        policySelectionText,
        votingPolicyComparisonLabel,
        votingPolicyComparisonLink,
      } = this.content;
      return {
        yourHoldingsHeading: replacePlaceholders(yourHoldingsHeading, this.mainframeData),
        yourHoldingsToggleButtonLabel: replacePlaceholders(yourHoldingsToggleButtonLabel, this.mainframeData),
        yourHoldingsText: replacePlaceholders(yourHoldingsText, this.mainframeData),
        yourHoldingsToggleButtonDefaultOn,
        policySelectionHeading: replacePlaceholders(policySelectionHeading, this.mainframeData),
        policySelectionText: replacePlaceholders(policySelectionText, this.mainframeData),
        votingPolicyComparisonLabel: replacePlaceholders(votingPolicyComparisonLabel, this.mainframeData),
        votingPolicyComparisonLink: prefixHTTP(votingPolicyComparisonLink),
      };
    },
    policiesData() {
      return this.policies.map((policy) => ({
        ...policy,
        policyDetailLink: prefixHTTP(policy.policyDetailLink),
        policyText: replacePlaceholders(policy.policyText, this.mainframeData),
      }));
    },
    disclosureData() {
      const { primaryText, micrositeTypeStyle } = this.brandData;
      const {
        disclosureHeader,
        disclosureHeaderHelp,
        disclosureHeaderText,
        disclosureCheckboxLabel,
        disclosureRequiredText,
        disclosureSubmitPolicyButtonLabel,
      } = this.content;
      return {
        disclosureHeader: replacePlaceholders(disclosureHeader, this.mainframeData),
        disclosureHeaderText: replacePlaceholders(disclosureHeaderText, this.mainframeData),
        disclosureCheckboxLabel: replacePlaceholders(disclosureCheckboxLabel, this.mainframeData),
        disclosureRequiredText: replacePlaceholders(disclosureRequiredText, this.mainframeData),
        disclosureSubmitPolicyButtonLabel: replacePlaceholders(disclosureSubmitPolicyButtonLabel, this.mainframeData),
        disclosureHeaderHelp: replacePlaceholders(disclosureHeaderHelp, this.mainframeData),
        tooltipColor: primaryText,
        tooltipTypeStyle: micrositeTypeStyle,
      };
    },
    faqsData() {
      return this.content.faqs.map((faq) => ({
        questionAnswer: replacePlaceholders(faq.questionAnswer, this.mainframeData),
        questionTitle: replacePlaceholders(faq.questionTitle, this.mainframeData),
      }));
    },
    footerData() {
      const { micrositeLogo, brandName } = this.brand;
      const {
        footerLogoAlignment,
        footerLogoSize,
        footerLogo,
        disclaimer,
        disclaimerLinks,
        footerLogos,
        footerDisclaimerCopy,
        footerLinks,
      } = this.content;
      return {
        logo: micrositeLogo,
        footerLogoAlignment,
        footerLogoSize,
        footerLogo,
        footerLinks,
        footerLogos,
        footerDisclaimerCopy,
        disclaimer: replacePlaceholders(disclaimer, this.mainframeData),
        disclaimerLinks: replacePlaceholders(disclaimerLinks, this.mainframeData),
        alt: brandName,
      };
    },
    socialData() {
      const { linkedinLink, youtubeLink, facebookLink, xLink } = this.brand;
      return [
        {
          name: "youtube",
          url: prefixHTTP(youtubeLink),
        },
        {
          name: "linkedin",
          url: prefixHTTP(linkedinLink),
        },
        {
          name: "x",
          url: prefixHTTP(xLink),
        },
        {
          name: "facebook",
          url: prefixHTTP(facebookLink),
        },
      ].filter((v) => v.url);
    },

    confirmationData() {
      const { thankYouText, backToPoliciesButtonLabel, previewConfirmation, thankYouHeader } = this.content;
      return {
        thankYouHeader: replacePlaceholders(thankYouHeader, this.mainframeData),
        backToPoliciesButtonLabel: replacePlaceholders(backToPoliciesButtonLabel, this.mainframeData),
        previewConfirmation,
        thankYouText: replacePlaceholders(thankYouText, this.mainframeData),
      };
    },
  },

  methods: {
    isEmptyOrNull(value) {
      return value == null || (typeof value === 'string' && value.trim().length === 0);
    },

    // Update the current fund's policy selection
    updateSelectedPolicy(index, value) {
      this.lastSelectedFund = value;
      if (index < 0) {
        // if index is -1, apply to all funds
        this.fundNames.forEach((fund) => (fund.selectedPolicy = value));
      } else {
        // otherwise, apply only to the selected fund
        this.fundNames[index].selectedPolicy = value;
      }
    },

    // Post the transactionId, emailToAddress (decoded from the token)
    // with the pipe-delimited selected policies and cusips
    submitSelection: function (toggleState) {
      this.defaultToggleState = toggleState;
      this.formSubmitStatus = FormSubmitEnum.IN_PROGRESS;
      const formUrl = window.location.href;

      // Check for blank policy before submit
      let selectedPolicy = this.fundNames.map((fund) => fund.selectedPolicy).join("|");
      if (selectedPolicy == "" || selectedPolicy.split("|").length != this.fundNames.length) {
        this.formSubmitError("Policy should not be blank");
        return;
      }

      // Check for blank cusip/security before submit
      let cusipClassList = this.fundNames.map((fund) => fund.security).join("|");
      if (cusipClassList == "" || cusipClassList.split("|").length != this.fundNames.length) {
        this.formSubmitError("Cusip should not be blank");
        return;
      }

      if (this.aemPreview) {
        this.formSubmitStatus = FormSubmitEnum.SUCCESS;
         this.showOptOut = true;
        console.log(formUrl + this.transactionId + this.policySelections);
        return;
      }

      axios
        .post(formUrl, this.transactionId + this.policySelections, {
          timeout: 5000,
        })
        .then((response) => {
          if (response.status == 200) {
            this.formSubmitStatus = FormSubmitEnum.SUCCESS;
            this.showOptOut = true;
          } else {
            this.formSubmitError(response);
          }
        })
        .catch((e) => {
          this.formSubmitError(e);
        });
    },

    // Log the error in console and display the error banner for 5 seconds
    formSubmitError: function (e) {
      if (typeof e == "string") console.log(e);

      this.formSubmitStatus = FormSubmitEnum.ERROR;
      setTimeout(() => {
        this.formSubmitStatus = FormSubmitEnum.NOT_SUBMITTED;
      }, 5000);
    },

    // Reset formSubmitStatus and scroll to top
    // when returning from the thank you page
    resetView: function () {
      this.formSubmitStatus = FormSubmitEnum.NOT_SUBMITTED;
      this.$nextTick(() => {
        this.scrollTo("top");  // Scroll to top after DOM update
      });
    },

    // Scroll to a component with defined "ref"
    scrollTo: function (refId) {     
        const section = this.$refs[refId];
        if (section && section.$el) 
          section.$el.scrollIntoView({block: "start", behavior: "smooth"});
        else 
          window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    // Create font-face rules if custom fonts are defined in AEM
    loadCustomFonts() {
      // Exit if there are no custom fonts
      if (!this.brand.specialFonts) {
        return;
      }

      // Create a style element
      const styleElement = document.createElement("style");
      styleElement.type = "text/css";

      // Constructing @font-face rules
      this.brand.specialFonts.forEach((font) => {
        font.fonts.forEach((variation) => {
          // Create a list of src URLs with format
          const sources = variation.files
            .map((file) => {
              if (file.endsWith(".otf")) {
                return `url('${file}') format('opentype')`;
              } else if (file.endsWith(".woff")) {
                return `url('${file}') format('woff')`;
              } else if (file.endsWith(".woff2")) {
                return `url('${file}') format('woff2')`;
              } else if (file.endsWith(".ttf")) {
                return `url('${file}') format('truetype')`;
              } else if (file.endsWith(".eot")) {
                return `url('${file}') format('embedded-opentype')`;
              } else if (file.endsWith(".svg")) {
                return `url('${file}') format('svg')`;
              }
            })
            .join(", ");

          // Determine the font-weight based on style
          let fontWeight = "normal"; // Default weight
          if (variation.style.includes("bold")) {
            fontWeight = 700;
          } else if (variation.style.includes("light")) {
            fontWeight = 100;
          }

          // Determine the font-style based on style (e.g., normal, italic)
          let fontStyle = "normal"; // Default style
          if (variation.style.includes("italic")) {
            fontStyle = "italic";
          } else if (variation.style.includes("oblique")) {
            fontStyle = "oblique";
          }

          // Construct @font-face rule
          const fontFaceRule = `
          @font-face {
            font-family: '${font.name}';
            src: ${sources};
            font-weight: ${fontWeight};
            font-style: ${fontStyle};
          }
        `;

          // Append the rule to the style element
          styleElement.innerHTML += fontFaceRule;
        });
      });

      // Append the style element to the head of the document
      document.head.appendChild(styleElement);
    },

    // Call getSurveyHistory API and load saved preferences in the policy selection
    getSurveyHistory() {
      console.log("Call Get Survey History");

      // No need to call ipm/getSurveyHistory API if in AEM preview mode
      // console.log(this.aemPreview);
      if (this.aemPreview || this.formSubmitStatus == FormSubmitEnum.EXPIRED) {
        return;
      }

      // Call /getSurveyHistory API to get the saved policy selections
      const formUrl = window.location.href;
      axios
        .post(formUrl + "/getSurveyHistory", this.transactionId, {
          timeout: 5000,
        })
        .then((response) => {
          try {
            var surveyHistory = response.data;
            this.loadSavedPolicies(surveyHistory);
          } catch (e) {
            console.error("surveyHistory=" + response.data);
          }
        })
        .catch((e) => {
          console.error(e.message);
          // Uncomment below for testing saved policies during development
          this.loadSavedPolicies(this.vueData.additionalAttributes.surveyHistory);
        });
    },

    // Load saved preferences in the policy selection
    loadSavedPolicies: function (surveyHistory) {
      // Nothing to load if surveyHistory is undefined
      if (!surveyHistory) {
        return;
      }

      // let surveyHistoryJSON = JSON.parse(JSON.stringify(surveyHistory));
      // let selectedPolicyIndex = surveyHistoryJSON[0].surveyResponse.policy;

      // if (selectedPolicyIndex == "") {
      //   return;
      // }

      // this.lastSelectedFund = selectedPolicyIndex;
      // this.savedPoliciesLoaded = this.savedPoliciesLoaded || selectedPolicyIndex != "";

      // if (this.savedPoliciesLoaded) {
      //   this.showOptOut = true;
      // }

      // // Nothing to load if surveyHistory is undefined
      // if (!surveyHistory) {
      //   return;
      // }

      // Load saved policies for each fund
      this.fundNames.forEach((fund) => {
        var savedPolicy = surveyHistory.find((survey) => survey.surveyResponse.cusip == fund.security);
        var validPolicy = this.policies.find(
          (policy) => savedPolicy && policy.policyIndex == savedPolicy.surveyResponse.policy
        );

        fund.selectedPolicy = validPolicy ? validPolicy.policyIndex : "";
        this.lastSelectedFund = fund.selectedPolicy;
        this.savedPoliciesLoaded = this.savedPoliciesLoaded || fund.selectedPolicy != "";
      });

      // Enable 3rd option "Opt out" if previous selection was loaded
      console.log("Saved Policies Loaded", this.savedPoliciesLoaded);
      this.showOptOut = this.savedPoliciesLoaded;

      // // Turn off defaultToggleState if saved policies are loaded
      // if (this.savedPoliciesLoaded && this.fundNames.length > 1) {
      //   this.defaultToggleState = false;
      // }

      // console.log("surveyHistory=", JSON.stringify(surveyHistory));
      // console.log("fundNames=", JSON.stringify(this.fundNames));
      // console.log("defaultToggleState=", JSON.stringify(this.defaultToggleState));
      // console.log("lastSelectedFund=", JSON.stringify(this.lastSelectedFund));
    },
  },

  // The created() lifecycle hook is called immediately after the Vue instance is created,
  // which means all data properties, methods, and watchers have been initialized.
  created() {
    // Dynamic page titles
    // This "magic" title assignment is to convert any encoded html tags to plain-text
    // No need to do this dynamic page title as Exxon is all static
    // var div = document.createElement("div");
    // div.innerHTML = replacePlaceholders(this.content.headlineText, this.mainframeData);
    // document.title = div.textContent;

    // Load custom fonts
    this.loadCustomFonts();

    // Call getSurveyHistory API and load saved policies
    this.getSurveyHistory();
  },
  mounted() {},
};
</script>

<style lang="scss">
@import url("https://fonts.cdnfonts.com/css/open-sans");
#app {
  min-width: min-content;
  max-width: 1100px;
  margin: 0 auto;
  border-top: 7px solid var(--headerBar);
}
</style>
